import { useQueryClient } from '@tanstack/react-query';
import { Checkbox, Form, Modal, Popconfirm } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { queryKeys } from '@/lib/react-query';
import { Location } from '@/models';
import { kmToMeters, metersToKm, showMessage } from '@/utils';
import { useCreateLocation, useDeleteLocation, useUpdateLocation } from '@/web-api/locations';

import { FormLocation, LocationForm } from './LocationForm';

type LocationModalProps = {
  mode: 'create' | 'edit';
  location?: Location | null;
  open: boolean;
  onClose: () => void;
};

export const LocationModal = ({ mode, location, open, onClose }: LocationModalProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [form] = Form.useForm<FormLocation>();
  const [isRageMode, setIsRageMode] = useState(false);

  const createLocationMutation = useCreateLocation();
  const updateLocationMutation = useUpdateLocation();
  const deleteLocationMutation = useDeleteLocation();

  const isEditMode = mode === 'edit';
  const isPending =
    createLocationMutation.isPending ||
    updateLocationMutation.isPending ||
    deleteLocationMutation.isPending;

  useEffect(() => {
    if (open) {
      if (isEditMode && location) {
        form.setFieldsValue({
          id: location.id,
          name: location.name,
          street: location.street,
          zip: location.zip,
          city: location.city,
          state: location.state,
          country: location.country,
          radius: metersToKm(location.radius),
        });
      } else {
        form.resetFields();
      }
    }
  }, [location, open, form, isEditMode]);

  const handleClose = () => {
    form.resetFields();
    setIsRageMode(false);
    onClose();
  };

  const onOk = () => form.submit();

  const onFormFinish = (formData: FormLocation) => {
    const requestData = {
      name: formData.name,
      street: formData.street,
      zip: formData.zip,
      city: formData.city,
      state: formData.state,
      country: formData.country,
      radius: kmToMeters(formData.radius),
    };

    if (isEditMode && location) {
      updateLocationMutation.mutate(
        { id: location.id, data: requestData },
        {
          onSuccess: () => {
            showMessage('success', t('forms:saved'));
            void queryClient.invalidateQueries({ queryKey: queryKeys.locations.all });
            handleClose();
          },
        }
      );
    } else {
      createLocationMutation.mutate(requestData, {
        onSuccess: () => {
          showMessage('success', t('forms:saved'));
          void queryClient.invalidateQueries({ queryKey: queryKeys.locations.all });
          form.resetFields();
          if (!isRageMode) {
            handleClose();
          }
        },
      });
    }
  };

  const onDelete = () => {
    if (!location) return;

    deleteLocationMutation.mutate(location.id, {
      onSuccess: () => {
        showMessage('success', t('forms:deleted'));
        void queryClient.invalidateQueries({ queryKey: queryKeys.locations.all });
        handleClose();
      },
    });
  };

  return (
    <Modal
      title={isEditMode ? t('common:editLocation') : t('common:newLocation')}
      open={open}
      onOk={onOk}
      onCancel={handleClose}
      okText={t('forms:save')}
      centered
      maskClosable={false}
      confirmLoading={isPending}
      destroyOnHidden={true}
      footer={(_, { OkBtn, CancelBtn }) => (
        <>
          {isEditMode && (
            <Popconfirm
              title={t('common:deleteLocationConfirm')}
              onConfirm={onDelete}
              okText={t('common:yes')}
              cancelText={t('common:no')}
            >
              <span
                style={{ float: 'left', color: '#ff4d4f', cursor: 'pointer' }}
                aria-label={t('common:delete')}
              >
                {t('common:delete')}
              </span>
            </Popconfirm>
          )}
          <CancelBtn />
          <OkBtn />
        </>
      )}
    >
      <LocationForm
        form={form}
        location={
          isEditMode && location
            ? {
                ...location,
                radius: metersToKm(location.radius),
              }
            : undefined
        }
        onFinish={onFormFinish}
        isEditingMode
      />
      {!isEditMode && (
        <Checkbox checked={isRageMode} onChange={(e) => setIsRageMode(e.target.checked)}>
          {t('common:createMoreLocations')}
        </Checkbox>
      )}
    </Modal>
  );
};
